import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import './comingSoon.css';
import { submitNotifyRequest } from './lib/notify';
import { resolveDomain } from './lib/resolveDomain';

type Status = 'idle' | 'submitting' | 'submitted' | 'error';

const SOCIAL_LINKS = [
  { label: 'Instagram', href: 'https://www.instagram.com/nawyrealestate/' },
  { label: 'Facebook', href: 'https://www.facebook.com/nawyrealestate' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/nawyestate/' },
] as const;

/**
 * Shared frame for the page's four inline icons — they differ only in size,
 * stroke weight, and paths. `stroke="currentColor"` lets CSS colour them.
 */
function Icon({ size = 20, strokeWidth = 2, children }: { size?: number; strokeWidth?: number; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function PinIcon() {
  return (
    <Icon size={16}>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </Icon>
  );
}

function MailIcon() {
  return (
    <Icon>
      <rect x="2" y="4" width="20" height="16" rx="3" />
      <path d="m2 7 10 7 10-7" />
    </Icon>
  );
}

function CheckIcon() {
  return (
    <Icon strokeWidth={2.5}>
      <path d="M20 6 9 17l-5-5" />
    </Icon>
  );
}

function AlertIcon() {
  return (
    <Icon size={18}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </Icon>
  );
}

export default function ComingSoon() {
  // Lazy useState initialisers: both are read once on mount and never change.
  // useMemo would not guarantee that, and module scope would freeze them at import.
  const [domain] = useState(() => resolveDomain(window.location.hostname));
  const [year] = useState(() => new Date().getFullYear());
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  // `status` is stale inside this render's closure, so it cannot guard against a
  // submit that arrives before React has re-rendered. A ref sees the change now.
  const inFlight = useRef(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current) return;

    inFlight.current = true;
    setStatus('submitting');
    try {
      const outcome = await submitNotifyRequest(email, domain);
      setStatus(outcome === 'ok' ? 'submitted' : 'error');
    } finally {
      inFlight.current = false;
    }
  }

  return (
    <div className="cs-wrap">
      <header className="cs-header">
        <img className="cs-logo" src="nawy-white-logo.png" alt="Nawy" width={1142} height={288} />
        <div className="cs-domain">
          <PinIcon />
          <span>{domain}</span>
        </div>
      </header>

      <main className="cs-main">
        <div className="cs-headline-group">
          <h1 className="cs-headline">
            Coming
            <br />
            <span className="cs-headline-accent">soon.</span>
          </h1>
          <p className="cs-headline-ar" dir="rtl" lang="ar">
            قريبًا
          </p>
        </div>

        <div className="cs-bottom">
          <div className="cs-capture">
            <p className="cs-lede">Nawy is opening a new home here. Leave your email and we will let you know the moment we launch.</p>
            <p className="cs-lede-ar" dir="rtl" lang="ar">
              ناوي تفتح بيتًا جديدًا هنا. اترك بريدك الإلكتروني وسنخبرك فور الإطلاق.
            </p>

            {status === 'submitted' ? (
              <div className="cs-confirmation" role="status">
                <CheckIcon />
                <span>You&rsquo;re on the list. We&rsquo;ll be in touch.</span>
              </div>
            ) : (
              <>
                <form className="cs-form" onSubmit={handleSubmit}>
                  <div className="cs-field">
                    <MailIcon />
                    <input
                      className="cs-input"
                      type="email"
                      name="email"
                      required
                      autoComplete="email"
                      aria-label="Your email address"
                      placeholder="Your email address"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </div>
                  <button className="cs-button" type="submit" disabled={status === 'submitting'}>
                    {status === 'submitting' ? 'Sending…' : 'Notify me'}
                  </button>
                </form>
                {status === 'error' && (
                  <p className="cs-error" role="alert">
                    <AlertIcon />
                    <span>We couldn&rsquo;t save your email just now. Please try again in a moment.</span>
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="cs-footer">
        <div className="cs-copyright">© {year} Nawy. All rights reserved.</div>
        <div className="cs-social">
          {SOCIAL_LINKS.map(({ label, href }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          ))}
        </div>
      </footer>
    </div>
  );
}
